import { request } from '@mairistem/tools';
import type {} from '@redux-devtools/extension';
import dayjs from 'dayjs';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { MODES_REGLEMENTS } from '@/constants';
import { getRecus } from '@/routes/recu';
import type { ApiResponseType, CacheMetadataType, RecuStatsType, RecuType } from '@/types';
import { CACHE_TTL, isCacheValid } from '@/utils/cache';

type RecuStatsStoreType = {
  data: RecuStatsType;
  loading: boolean;
  /** Requête en cours, y compris lors d'un rafraîchissement silencieux */
  fetching: boolean;
  error: string | null;
  cache: CacheMetadataType;

  fetch: (regieId: number, hasCBI: boolean, hasPRI: boolean, force?: boolean) => Promise<void>;
  clear: () => void;
};

const useRecuStatsStore = create<RecuStatsStoreType>()(
  devtools(
    (set, get) => ({
      data: {
        nombreEncaissements: 0,
        nombreAnnulations: 0,
        nombreCBI: 0,
        nombrePRI: 0,
      },
      loading: false,
      fetching: false,
      error: null,
      cache: {
        timestamp: null,
        regieId: null,
      },

      fetch: async (regieId: number, hasCBI: boolean, hasPRI: boolean, force = false) => {
        // Guard : éviter fetch concurrent
        if (get().fetching) return;

        // Skip si cache valide et pas de force
        const { cache } = get();
        if (!force && isCacheValid(cache.timestamp, CACHE_TTL.RECU_STATS, cache.regieId, regieId)) {
          return;
        }

        // Ne montrer le loading que si on n'a pas encore de données
        // Pour un refresh invisible à l'utilisateur
        const hasData = get().cache.timestamp !== null;
        set({ loading: !hasData, fetching: true, error: null });

        try {
          const today = dayjs().format('YYYY-MM-DD');

          // 4 appels API en parallèle
          const [encaissementsRes, annulationsRes, cbiRes, priRes] = (await Promise.all([
            // Encaissements du jour (reçus non annulés créés aujourd'hui)
            request.get(
              getRecus({
                perPage: 1,
                dateAnnulation: 'isNull()',
                dateCreation: today,
              }),
            ),

            // Annulations du jour
            request.get(
              getRecus({
                perPage: 1,
                dateAnnulation: today,
              }),
            ),

            // Paiements CB internet (si mode actif)
            hasCBI
              ? request.get(
                  getRecus({
                    perPage: 1,
                    dateAnnulation: 'isNull()',
                    dateCreation: today,
                    codeModeReglement: MODES_REGLEMENTS.CARTE_BANCAIRE_INTERNET,
                  }),
                )
              : Promise.resolve({ meta: { total: 0 }, data: [] }),

            // Prélèvements internet (si mode actif)
            hasPRI
              ? request.get(
                  getRecus({
                    perPage: 1,
                    dateAnnulation: 'isNull()',
                    dateCreation: today,
                    codeModeReglement: MODES_REGLEMENTS.PRELEVEMENT_INTERNET,
                  }),
                )
              : Promise.resolve({ meta: { total: 0 }, data: [] }),
          ])) as ApiResponseType<RecuType>[];

          set({
            data: {
              nombreEncaissements: encaissementsRes?.meta?.total || 0,
              nombreAnnulations: annulationsRes?.meta?.total || 0,
              nombreCBI: cbiRes?.meta?.total || 0,
              nombrePRI: priRes?.meta?.total || 0,
            },
            loading: false,
            fetching: false,
            cache: {
              timestamp: Date.now(),
              regieId,
            },
          });
        } catch (error) {
          set({
            loading: false,
            fetching: false,
            error: (error as Error).message || 'Erreur lors du chargement des statistiques',
          });
        }
      },

      clear: () => {
        set({
          data: {
            nombreEncaissements: 0,
            nombreAnnulations: 0,
            nombreCBI: 0,
            nombrePRI: 0,
          },
          loading: false,
          fetching: false,
          error: null,
          cache: { timestamp: null, regieId: null },
        });
      },
    }),
    { name: 'RecuStatsStore' },
  ),
);

export default useRecuStatsStore;
